import type { PaginationModel, SortModel } from '@/components'
import {
  PagedUserEmployeeSchema,
  UserEmployeeSchema,
  type UserEmployee,
  type UserEmployeePage
} from '@/features/user/api/user-employee.data.ts'
import { apiClient, handleResponse } from '@/utils/axios-client.ts'
import { buildSearchParameters } from '@/utils/search-paramater-url.ts'
import { z } from 'zod'
import {
  UserRequestSchema,
  UserInfosSchema,
  type UserRequest,
  RoleArraySchema,
  type UserInfo,
  type UserSettingsForm,
  type UserRoleType,
  type RoleList
} from './user.data.ts'

const USERS_ENDPOINT = '/api/v1/users'
const EMPLOYEES_ENDPOINT = '/api/v1/employees'

export const fetchUsersEmployees = async (
  sortModel?: SortModel<UserEmployee>,
  pagination?: PaginationModel,
  search?: string,
  roles?: UserRoleType[],
  siteIds?: string[],
  statuses?: string[],
  signal?: AbortSignal
): Promise<UserEmployeePage> => {
  return await handleResponse(
    () =>
      apiClient.get(`/api/v1/employees`, {
        params: buildSearchParameters(
          {
            roles: roles ?? undefined,
            statuses: statuses ?? undefined,
            search: search ?? undefined,
            siteIds: siteIds ?? undefined
          },
          {
            sorting: sortModel,
            pagination: pagination
          }
        ),
        signal
      }),
    PagedUserEmployeeSchema
  )
}

export const fetchEmployeeById = async (
  userId: string,
  signal?: AbortSignal
): Promise<UserEmployee> => {
  return await handleResponse(
    () => apiClient.get(`${EMPLOYEES_ENDPOINT}/${userId}`, { signal }),
    UserEmployeeSchema
  )
}

export const getUserInfos = async (token?: string): Promise<UserInfo> => {
  return await handleResponse(
    () =>
      apiClient.get(`${USERS_ENDPOINT}/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }),
    UserInfosSchema
  )
}

export const saveUserSettings = async (settings: UserSettingsForm): Promise<void> => {
  return await handleResponse(
    () => apiClient.put(`${USERS_ENDPOINT}/me/settings`, settings),
    z.any()
  )
}

export const getInvitableRoles = async (signal?: AbortSignal): Promise<RoleList> => {
  return await handleResponse(
    () => apiClient.get(`${USERS_ENDPOINT}/invitable-roles`, { signal }),
    RoleArraySchema
  )
}

export const getReadableRoles = async (signal?: AbortSignal): Promise<RoleList> => {
  return await handleResponse(
    () => apiClient.get(`${USERS_ENDPOINT}/readable-roles`, { signal }),
    RoleArraySchema
  )
}

export const checkActivationToken = async (token: string): Promise<boolean> => {
  try {
    await handleResponse(
      () => apiClient.get(`/api/v1/user-activations/${token}/validity`),
      z.any()
    )
    return true
  } catch {
    return false
  }
}

export const createUser = async (userData: UserRequest): Promise<void> => {
  // Validate the request data
  const validationResult = UserRequestSchema.safeParse(userData)

  if (!validationResult.success) {
    throw new Error(`Invalid user data: ${z.prettifyError(validationResult.error)}`)
  }

  return handleResponse(
    () => apiClient.post(EMPLOYEES_ENDPOINT, validationResult.data),
    z.any()
  )
}

export const updateUser = async (
  userId: string,
  userData: UserRequest
): Promise<void> => {
  // Validate the request data
  const validationResult = UserRequestSchema.safeParse(userData)

  if (!validationResult.success) {
    throw new Error(`Invalid user data: ${z.prettifyError(validationResult.error)}`)
  }

  return handleResponse(
    () => apiClient.put(`${EMPLOYEES_ENDPOINT}/${userId}`, validationResult.data),
    z.any()
  )
}

export const resentActivation = async (userId: string): Promise<void> => {
  return handleResponse(
    () => apiClient.post(`${USERS_ENDPOINT}/${userId}/activations`),
    z.any()
  )
}

export const suspendUser = async (userId: string): Promise<void> => {
  return handleResponse(
    () => apiClient.post(`/api/v1/employees/${userId}/suspend`),
    z.any()
  )
}

export const resumeUser = async (userId: string): Promise<void> => {
  return handleResponse(
    () => apiClient.post(`/api/v1/employees/${userId}/resume`),
    z.any()
  )
}
